import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  SystemCurrencyCell,
  TermsCurrencyCell,
  systemCurrencyHeader,
  termsCurrencyHeader,
  useTermsColumnsVisibleByDefault
} from "@/entities/dataTables/currencyColumns";
import { useAccountCurrencyResolution } from "@/services/hooks/useCurrencyConversion";
import { CellContext, ColumnDef, SortingState } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import DataTable from "@/components/controls/DataTable/DataTable";
import DataTableToolbar from "@/components/controls/DataTableToolbar";
import ToolbarActions from "@/components/controls/ToolbarActions/ToolbarActions";
import ServicesBottomKPIBar from "@/components/controls/BottomKPIBar/ServicesBottomKPIBar";
import IconContainer from "@/components/ui/icon-container";
import { StatusBadge } from "@/components/ui/status-badge";
import { Bullet, BulletDashed } from "@dreamhub/designsystem/icons";
import { useEntityExport } from "@/services/hooks/entities/useEntityExport";
import {
  ExportStateChangeHandler,
  useNotifyExportState
} from "@/services/hooks/entities/useExportButtonState";
import {
  SERVICES_EXPORT_EXCLUDED_COLUMNS,
  SERVICES_SORT_FIELD_BY_COLUMN,
  ServiceColumn,
  ServiceData,
  ServiceSourceType,
  ServiceStatus
} from "@/entities/services/types";
import {
  buildDisplayValueById,
  groupFieldValues,
  buildGroupSummaries,
  rekeyGroupSummaries
} from "@/components/controls/DataTable/groupSummaries";
import {
  useFilteredServices,
  useServiceFields
} from "@/entities/services/hooks";
import {
  displayTotalCurrency,
  formatNumber,
  formatPercentage
} from "@/utils/numbers";
import { EmptyFilters, Filters } from "@/services/api/types";
import {
  getTempView,
  getDefaultColumnsStateWithCustomFields,
  mapSelectedCustomColumnsToTableColumns,
  mapCustomFieldsToFilterOptions,
  resolveLinkedCellValue,
  getFlexSnapshot,
  mergeMissingDefaultColumns,
  resolveServerGroupField
} from "@/entities/dataTables/helpers";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";
import { ColumnData } from "@/entities/dataTables/types";
import { resolveSortField } from "@/entities/dataTables/sorting";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import {
  EmptyView,
  ViewDataConfiguration,
  ViewType
} from "@/entities/views/types";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { useUserFilterValues } from "@/entities/users/useUserSelect";
import { useUsers } from "@/entities/users/hooks";
import UserAvatar from "@/components/controls/UserAvatar";
import { useProducts, useCurrencies } from "@/entities/settings/hooks";
import { useServiceItemTypes } from "@/pages/settings/pages/products-v2/hooks";
import { useDealStages } from "@/entities/settings/deal/hooks";
import { DealStatus } from "@/pages/deals/types";
import { dealStatusColors } from "@/pages/deals/components/DealsTable/DealsTable";
import {
  ChargeType,
  ServiceDuration,
  ServiceItemType
} from "@/pages/settings/pages/products-v2/types";
import { getServiceDurationFromNumber } from "@/pages/settings/pages/products-v2/utils";
import { Product } from "@/pages/settings/components/settingsItems/hooks/types";
import { SettingsFieldType } from "@/pages/settings/types";
import { getEnumTKey } from "@/utils/strings";
import { tWithFallback } from "@/utils/i18n";
import { toLocalDate } from "@/utils/datetime";
import { formatDistanceStrict } from "date-fns";
import { isEmpty } from "lodash";
import toast from "react-hot-toast";
import { buildDeltaColumns } from "@/entities/dataTables/compareTo/deltaColumns";
import { buildCompareExport } from "@/entities/dataTables/compareTo/compareExport";
import { formatTimeframeLabel } from "@/entities/dataTables/compareTo/periods";
import { useServicesComparison } from "@/entities/dataTables/compareTo/useServicesComparison";
import {
  DEFAULT_COMPARE_PERIOD,
  type CompareToSelection
} from "@/entities/dataTables/compareTo/types";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { toDateOnlyISO } from "@/utils/datetime";
import {
  readServiceRowStart,
  SERVICE_COMPARE_COLUMN_IDS,
  SERVICE_COMPARE_SLOTS,
  SERVICE_COMPARE_TERMS_COLUMN_IDS,
  SERVICE_COMPARE_TRANSLATION_KEY
} from "./compareToColumns";
import { camelizeFieldKey } from "@/utils/fieldKeys";
import ServiceNotePopover from "@/components/layouts/ServiceNotePopover/ServiceNotePopover";
import {
  FINANCIALS_WRITE_PERMISSION,
  useHasPermission
} from "@/services/auth/AuthContext";

const TABLE_ID = "services-table";

// Column IDs with custom cell rendering or field_type=header (excluded from the generic
// metadata pipeline). All other columns come from the revenue_items_fields pipeline.
const HARDCODED_COLUMN_IDS: Set<string> = new Set([
  ServiceColumn.SERVICE_ITEM_NAME, // field_type=header, not surfaced by generic pipeline
  ServiceColumn.COMPANY_NAME,
  ServiceColumn.PRODUCT_NAME,
  ServiceColumn.STATUS_NAME, // custom Bullet-icon rendering
  ServiceColumn.CURRENCY,
  ServiceColumn.CSM_OWNER,
  ServiceColumn.AE_OWNER,
  ServiceColumn.BILLING_FREQUENCY, // excluded from pipeline; rendered via hardcoded ColumnDefmnDef
  ServiceColumn.SERVICE_ITEM_ID,
  ServiceColumn.SERVICE_ITEM_PRICING_ID,
  "pricing", // computed total/quantity; no accessorKey
  // FK/ID variants and display-name variants excluded so the pipeline doesn't
  // create duplicate columns/filters alongside the hardcoded definitions above.
  ServiceColumn.COMPANY_ID,
  ServiceColumn.PRODUCT_ID,
  ServiceColumn.STATUS,
  ServiceColumn.CUSTOM_SERVICE_DURATION,
  ServiceColumn.CUSTOM_SERVICE_DURATION_DISPLAY,
  ServiceColumn.AUTO_RENEWAL,
  ServiceColumn.AUTO_RENEWAL_DISPLAY,
  ServiceColumn.SERVICE_TYPE,
  ServiceColumn.CHARGE_TYPE,
  ServiceColumn.DISCOUNT_AMOUNT,
  ServiceColumn.PRICING,
  ServiceColumn.PRICING_TERMS,
  ServiceColumn.NOTE
]);

// `total`/`totalLocal` reach the table through the revenue_items_fields pipeline
// rather than a hardcoded def. The generic renderer would print both under one
// configured symbol; route them through the split cells so the system half shows
// the account's currency and the terms half the row's own.
const renderSystemCurrencyCell = (amount: number): ReactNode => (
  <SystemCurrencyCell amount={amount} />
);

const renderTermsCurrencyCell = (
  amount: number,
  row: ServiceData
): ReactNode => (
  <TermsCurrencyCell amount={amount} currency={row.currency ?? null} />
);

const CURRENCY_FIELD_RESOLVERS: Record<
  string,
  (amount: number, row: ServiceData) => ReactNode
> = {
  [ServiceColumn.TOTAL]: renderSystemCurrencyCell,
  [ServiceColumn.TOTAL_TERMS]: renderTermsCurrencyCell
};

// The terms half of every split money pair on this table. Off by default for an
// account that has never signed in another currency — see
// useTermsColumnsVisibleByDefault.
const TERMS_COLUMN_IDS: ReadonlySet<string> = new Set<string>([
  ServiceColumn.TOTAL_TERMS,
  ServiceColumn.PRICING_TERMS
]);

// Excel export: each terms column is priced in the row's own contract currency.
const EXPORT_CURRENCY_FIELD_BY_COLUMN: Readonly<Record<string, string>> =
  Object.fromEntries([...TERMS_COLUMN_IDS].map((id) => [id, "currency"]));

// What the table starts on and Reset returns to; shared so all three agree.
const DEFAULT_COMPARE_TO: CompareToSelection = {
  period: DEFAULT_COMPARE_PERIOD
};


const sortByOptions = [
  ServiceColumn.SERVICE_ITEM_NAME,
  ServiceColumn.COMPANY_NAME,
  ServiceColumn.CONTRACT_NAME,
  ServiceColumn.SERVICE_TYPE,
  ServiceColumn.CHARGE_TYPE,
  ServiceColumn.STATUS_NAME,
  ServiceColumn.CURRENCY,
  ServiceColumn.SERVICE_START_DATE,
  ServiceColumn.TOTAL
];

// The renewal type as displayed; also the grouping key, so rows that read the
// same land in one group whether they carry the flag or only the display text.
const renewalTypeLabel = (row: ServiceData) =>
  row.autoRenewal == null
    ? row.autoRenewalDisplay || ""
    : tWithFallback(
        `services.renewalType.${row.autoRenewal ? "auto" : "manual"}`,
        row.autoRenewalDisplay
      );

const serviceStatusColors: Record<ServiceStatus, string> = {
  [ServiceStatus.ACTIVE]: "var(--charts-positive)",
  [ServiceStatus.UPCOMING]: "var(--border-field-default)",
  [ServiceStatus.EXPIRED]: "var(--charts-negative)",
  [ServiceStatus.ONE_TIME]: "var(--charts-purple-strong)",
  [ServiceStatus.DRAFT]: "var(--border-field-default)"
};

// Upcoming uses a dashed bullet; the rest use the filled bullet.
const getServiceStatusIcon = (status: ServiceStatus) =>
  status === ServiceStatus.UPCOMING ? <BulletDashed /> : <Bullet />;

interface BaseFilterOptionsArgs {
  t: (key: string) => string;
  products: Product[];
  userFilterValues: { value: string; label: string }[];
  currencies: string[];
  serviceItemTypes: ServiceItemType[];
  dealStages: { id: number; name: string }[];
}

const buildBaseFilterOptions = ({
  t,
  products,
  userFilterValues,
  currencies,
  serviceItemTypes,
  dealStages
}: BaseFilterOptionsArgs): DataTableFilter[] => {
  const all: DataTableFilter[] = [
    { key: ServiceColumn.SERVICE_ITEM_NAME, filterType: { type: "text" } },
    {
      key: "companyId",
      filterType: {
        type: "map",
        values: [],
        sort: true,
        showSearch: true,
        entity: EntityType.COMPANY
      }
    },
    {
      key: "productId",
      filterType: {
        type: "map",
        values: products.map((product) => ({
          value: product.id,
          label: product.name
        })),
        sort: true,
        showSearch: true
      }
    },
    {
      key: "status",
      columnId: ServiceColumn.STATUS_NAME,
      excludeNoValue: true,
      filterType: {
        type: "enum",
        values: {
          enumType: ServiceStatus,
          entityType: "services",
          valueType: "statusValues",
          getIcon: (status: ServiceStatus) => (
            <IconContainer
              size="xs"
              style={{ color: serviceStatusColors[status] }}
            >
              {getServiceStatusIcon(status)}
            </IconContainer>
          )
        }
      }
    },
    {
      key: ServiceColumn.CSM_OWNER,
      filterType: {
        type: "map",
        values: userFilterValues,
        showAvatar: true,
        sort: true,
        showSearch: true,
        entity: EntityType.USER
      }
    },
    {
      key: ServiceColumn.AE_OWNER,
      filterType: {
        type: "map",
        values: userFilterValues,
        showAvatar: true,
        sort: true,
        showSearch: true,
        entity: EntityType.USER
      }
    },
    { key: ServiceColumn.SOURCE, filterType: { type: "text" } },
    {
      key: ServiceColumn.SOURCE_TYPE,
      filterType: {
        type: "enum",
        values: {
          enumType: ServiceSourceType,
          entityType: "services",
          valueType: "sourceTypeValues"
        }
      }
    },
    {
      key: "customServiceDuration",
      columnId: ServiceColumn.CUSTOM_SERVICE_DURATION_DISPLAY,
      filterType: {
        type: "enum",
        values: {
          enumType: ServiceDuration,
          entityType: "services",
          valueType: "serviceDurationValues"
        }
      }
    },
    {
      key: "autoRenewal",
      columnId: ServiceColumn.AUTO_RENEWAL_DISPLAY,
      filterType: {
        type: "boolean",
        label: t("services.autoRenewalFilter"),
        trueLabel: "services.renewalType.auto",
        falseLabel: "services.renewalType.manual"
      }
    },
    {
      key: ServiceColumn.CURRENCY,
      filterType: {
        type: "map",
        values: currencies.map((code) => ({ value: code, label: code })),
        sort: true,
        showSearch: true
      }
    },
    { key: "total", filterType: { type: "numeric", isMonetary: true } },
    { key: "quantity", filterType: { type: "numeric" } },
    { key: "discountAmount", filterType: { type: "numeric" } },
    { key: "serviceStartDate", filterType: { type: "date" } },
    { key: "billingStartDate", filterType: { type: "date" } },
    { key: "customOptOutDate", filterType: { type: "date" } },
    {
      key: "serviceType",
      filterType: {
        type: "map",
        values: serviceItemTypes.map((type) => ({
          value: type.id,
          label: type.name
        })),
        sort: true,
        showSearch: true
      }
    },
    {
      key: "chargeType",
      filterType: {
        type: "map",
        values: [
          {
            value: ChargeType.RECURRING,
            label: t("services.chargeTypeValues.recurring")
          },
          {
            value: ChargeType.ONE_TIME,
            label: t("services.chargeTypeValues.oneTime")
          }
        ]
      }
    },
    {
      key: "dealsStage",
      filterType: {
        type: "map",
        values: dealStages.map((stage) => ({
          value: stage.id,
          label: stage.name
        })),
        sort: true,
        showSearch: true
      }
    },
    {
      // Keeps "No value" so excluding Closed Lost still shows contract-only services.
      key: ServiceColumn.DEALS_STATUS,
      filterType: {
        type: "enum",
        values: {
          enumType: DealStatus,
          entityType: "stakeholders.dealsModal.table",
          valueType: "status",
          getIcon: (status: DealStatus) => (
            <IconContainer
              size="xs"
              style={{ color: dealStatusColors[status] }}
            >
              <Bullet />
            </IconContainer>
          )
        }
      }
    },
    {
      // Keeps "No value" so the filter does not hide services whose company has no parent.
      key: ServiceColumn.PARENT_COMPANY,
      customFieldLabel: t("services.parentCompany"),
      filterType: {
        type: "map",
        values: [],
        sort: true,
        showSearch: true,
        entity: EntityType.COMPANY
      }
    },
    { key: "contractName", filterType: { type: "text" } },
    { key: "nextBillingDate", filterType: { type: "date" } },
    { key: "billingEndDate", filterType: { type: "date" } },
    { key: "serviceEndDate", filterType: { type: "date" } }
  ];
  return all;
};

const getCurrencySymbol = (code: string) => {
  const { currencySign } = formatNumber(
    0,
    0,
    true,
    false,
    undefined,
    undefined,
    true,
    code
  );
  return currencySign;
};

// A component of its own rather than an inline cell: TanStack renders a cell
// function as a component, so a new one from each columns rebuild would
// remount the cell and close an open popover.
const ServiceNoteCell: FC<CellContext<ServiceData, unknown>> = ({ row }) => {
  const { t } = useTranslation();
  const canEditFinancials = useHasPermission(FINANCIALS_WRITE_PERMISSION);
  const { id, contractId, note, noteUpdatedBy, dealId, dealsStatus } =
    row.original;
  if (!contractId) return null;
  // The server's note lock: on standalone contracts and closed-won deals,
  // editing needs the financials permission.
  const financialsLocked =
    !canEditFinancials && (!dealId || dealsStatus === DealStatus.CLOSED_WON);
  return (
    <ServiceNotePopover
      contractId={contractId}
      revenueItemId={id}
      note={note}
      noteUpdatedBy={noteUpdatedBy}
      editDisabled={financialsLocked}
      editDisabledTooltipText={t("common.noEditPermission")}
    />
  );
};

interface Props {
  onExportStateChange?: ExportStateChangeHandler;
}

const ServicesTable: FC<Props> = ({ onExportStateChange }) => {
  const { t } = useTranslation();
  const { accountCurrency, isResolved: accountCurrencyResolved } =
    useAccountCurrencyResolution();
  const {
    visibleByDefault: termsColumnsVisibleByDefault,
    isResolved: currencyUsageResolved
  } = useTermsColumnsVisibleByDefault();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [filters, setFilters] = useState<Filters>(EmptyFilters);
  const [searchTerm, setSearchTerm] = useState("");
  const [groupBy, setGroupBy] = useState<string[]>([]);
  const [columnsState, setColumnsState] = useState<ColumnData[]>(() =>
    getFlexSnapshot(ViewType.SERVICES_TABLE)
  );
  // Track which view id has had labels reconciled so we re-run on view switch.
  const reconciledForViewRef = useRef<string | null>(null);
  const [selectedView, setSelectedView] = useState<ViewDataConfiguration>(
    EmptyView(ViewType.SERVICES_TABLE)
  );
  const [compareTo, setCompareTo] =
    useState<CompareToSelection>(DEFAULT_COMPARE_TO);

  // Branching on `enabled` alone renders the flag-off table first, then remounts.
  const { enabled: isCompareToFlagOn, loading: compareToFlagLoading } =
    useFeatureFlag(FeatureFlags.CRM_9208_CONTRACT_AMENDMENTS);
  const compareToEnabled = isCompareToFlagOn && !compareToFlagLoading;
  const { enabled: showNoteColumn } = useFeatureFlag(
    FeatureFlags.CRM_10582_SERVICE_NOTES
  );

  const compareTimeframeLabel = useMemo(
    () => formatTimeframeLabel(compareTo, t),
    [compareTo, t]
  );

  // Export hook is defined below useFilteredServices so totalRows reflects
  // the row count the user is exporting (adaptive poll cadence).

  const sortField = resolveSortField(sorting, SERVICES_SORT_FIELD_BY_COLUMN);
  const sortAsc = sorting[0] ? !sorting[0].desc : undefined;

  const { data: fieldsData } = useServiceFields();
  const { data: productsData } = useProducts();
  const { data: currenciesData } = useCurrencies();
  const { data: serviceItemTypesData } = useServiceItemTypes();
  const { data: dealStagesData } = useDealStages();
  const products = useMemo(
    () => productsData?.products ?? [],
    [productsData?.products]
  );
  const currencies = useMemo(() => currenciesData ?? [], [currenciesData]);
  const serviceItemTypes = useMemo(
    () => serviceItemTypesData ?? [],
    [serviceItemTypesData]
  );
  const dealStages = useMemo(
    () => dealStagesData?.stages ?? [],
    [dealStagesData?.stages]
  );
  const userFilterValues = useUserFilterValues();
  const groupField = groupBy[0];
  const serverGroupField = resolveServerGroupField(
    groupField,
    fieldsData?.fields
  );
  const serverGroupFieldKey = serverGroupField?.fieldKey;
  const groupsAreUsers =
    serverGroupField?.displayType === FieldDisplayType.USERS;

  const {
    services,
    aggregations,
    rawAggregations,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
    isAggregationsLoading,
    isAggregationsError,
    isError,
    refetch,
    total
  } = useFilteredServices(
    sortField,
    sortAsc,
    filters,
    searchTerm,
    true,
    serverGroupFieldKey
  );

  const ownerIds = useMemo(() => {
    const ids = new Set<string>();
    services.forEach((row) => {
      if (row.csmOwner) ids.add(row.csmOwner);
      if (row.aeOwner) ids.add(row.aeOwner);
    });
    // Grouping by a user names a group per user in the whole filtered set, so
    // the names have to cover the groups the response enumerated too — an owner
    // whose rows are still paging in has no name anywhere else on the page.
    if (groupsAreUsers) {
      groupFieldValues(rawAggregations).forEach((id) => ids.add(id));
    }
    return Array.from(ids);
  }, [services, groupsAreUsers, rawAggregations]);

  const { users: ownerUsers } = useUsers(ownerIds, {
    enabled: ownerIds.length > 0
  });

  const userById = useMemo(() => {
    const map = new Map<string, (typeof ownerUsers)[number]>();
    ownerUsers.forEach((user) => {
      if (user?.id) map.set(user.id, user);
    });
    return map;
  }, [ownerUsers]);

  // Every group option is offered from the field registry, so the registry also
  // answers which stored column backs it — the server can then count and sum
  // each group up front instead of the table totalling whatever rows infinite
  // scroll has reached.
  const displayValueById = useMemo(
    () =>
      serverGroupFieldKey && serverGroupFieldKey !== groupField
        ? buildDisplayValueById(services, serverGroupFieldKey, groupField)
        : undefined,
    [services, serverGroupFieldKey, groupField]
  );

  const groupSummaries = useMemo(() => {
    if (!serverGroupFieldKey) return undefined;
    const byFieldValue = buildGroupSummaries(
      rawAggregations,
      ["total"],
      isAggregationsLoading
    );
    // The column shows a name the server grouped by id, so the summaries follow
    // it. An id no loaded row carries keeps its own key, which matches no group
    // rather than the wrong one.
    return displayValueById
      ? rekeyGroupSummaries(byFieldValue, (id) => displayValueById[id] ?? id)
      : byFieldValue;
  }, [
    serverGroupFieldKey,
    displayValueById,
    rawAggregations,
    isAggregationsLoading
  ]);

  const serviceIds = useMemo(() => services.map((row) => row.id), [services]);

  const {
    byRowId: comparisonsByServiceId,
    notFoundIds: comparisonNotFoundIds,
    periodStart: comparisonPeriodStart,
    isError: isComparisonError
  } = useServicesComparison(serviceIds, compareTo, compareToEnabled);

  // A Set so the per-cell lookup in the column builder is O(1).
  const comparisonNotFoundIdSet = useMemo(
    () => new Set(comparisonNotFoundIds),
    [comparisonNotFoundIds]
  );

  // Blank is how "no prior record" renders, so a silent failure reads as no change.
  useEffect(() => {
    if (!isComparisonError) return;
    toast.error(t("services.compareTo.loadFailed"));
  }, [isComparisonError, t]);

  // Empty while the flag is closed, keeping every consumer below unchanged.
  const compareToColumnIds = useMemo(
    () => (compareToEnabled ? SERVICE_COMPARE_COLUMN_IDS : []),
    [compareToEnabled]
  );

  // The export computes the Δ and Impact columns server-side for the period
  // sent in its filter, so they export while the flag is open. Once it closes
  // they stay excluded: a saved view built while it was open keeps its Δ ids.
  const compareExport = useMemo(
    () =>
      buildCompareExport({
        enabled: compareToEnabled,
        selection: compareTo,
        allColumnIds: SERVICE_COMPARE_COLUMN_IDS,
        exportableColumnIds: compareToColumnIds,
        translationKey: SERVICE_COMPARE_TRANSLATION_KEY,
        t
      }),
    [compareToEnabled, compareTo, compareToColumnIds, t]
  );

  // A view saved while the notes flag was on still lists `note`, and nothing
  // drops it from columnsState once the flag is off — so the flag gates the
  // export here, the same as it gates the column.
  const exportExcludedColumns = useMemo(
    () => [
      ...SERVICES_EXPORT_EXCLUDED_COLUMNS,
      ...compareExport.excludedColumnIds,
      ...(showNoteColumn ? [] : [ServiceColumn.NOTE])
    ],
    [compareExport, showNoteColumn]
  );

  // The API has no field to sort these by, and the Sort by list is built from
  // defaultColumnsState — so they reach it the moment they reach the picker.
  const compareToSortMenuExclusions = useMemo(
    () => new Set(compareToColumnIds),
    [compareToColumnIds]
  );

  // A user column holds the id and renders the name, so a group keeps the id as
  // its key and takes its heading from the users fetched for every group the
  // response named — an owner with no rows on screen yet still reads as a name.
  const knownGroups = useMemo(() => {
    if (!groupSummaries) return undefined;
    const resolved = displayValueById
      ? new Set(Object.values(displayValueById))
      : undefined;
    return Object.keys(groupSummaries)
      .filter((key) => !resolved || key === "" || resolved.has(key))
      .map((value) => ({
        value,
        label:
          value === ""
            ? t("tables.notAvailable")
            : groupsAreUsers
              ? userById.get(value)?.displayName || t("tables.notAvailable")
              : value
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [groupSummaries, displayValueById, groupsAreUsers, userById, t]);

  const { exportTo: exportServices, isExporting: isExportingServices } =
    useEntityExport({
      createRoute: "contracts/revenueItems/export/create",
      statusRoute: "contracts/revenueItems/export/status",
      tableName: "services",
      filters,
      sorting,
      // Mirrors the mapping the grid applies to its own sort request, so the CSV
      // comes out in the order shown on screen.
      sortFieldByColumn: SERVICES_SORT_FIELD_BY_COLUMN,
      searchTerm,
      columnsState,
      totalRows: total,
      // Include custom-field filter options so the export can resolve
      // single/multi-select codes (Deal Type, Rev. Share …) to their labels.
      getFilterOptions: () => exportFilterOptions,
      excludeFromExport: exportExcludedColumns,
      // The column renders billingFrequencyDisplay via accessorFn but its id is
      // eqPaymentsBillingFrequency (to match HARDCODED_COLUMN_IDS). Send the
      // display field to the export so the CSV shows the consolidated value
      // (Monthly/Quarterly/custom) instead of the raw equal-payments unit.
      exportColumnIdMap: {
        [ServiceColumn.BILLING_FREQUENCY]: "billingFrequencyDisplay"
      },
      columnCurrencyFields: EXPORT_CURRENCY_FIELD_BY_COLUMN,
      getExtraColumnValueLabels: () => ({
        [ServiceColumn.CHARGE_TYPE]: {
          [String(ChargeType.RECURRING)]: t(
            "services.chargeTypeValues.recurring"
          ),
          [String(ChargeType.ONE_TIME)]: t("services.chargeTypeValues.oneTime")
        },
        [ServiceColumn.SERVICE_TYPE]: Object.fromEntries(
          serviceItemTypes.map((type) => [String(type.id), type.name])
        ),
        ...compareExport.impactValueLabels
      }),
      getExtraFilterParams: compareExport.getFilterParams,
      // Read at export time, so `columnIds` (declared below) is initialised.
      getColumnLabelOverrides: () => compareExport.pickColumnLabels(columnIds)
    });

  useNotifyExportState(
    onExportStateChange,
    exportServices,
    isExportingServices
  );

  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage && !isError) fetchNextPage();
  }, [isFetchingNextPage, hasNextPage, isError, fetchNextPage]);

  const handleClearFilters = useCallback(() => setFilters(EmptyFilters), []);

  useEffect(() => {
    const tempView = getTempView(selectedView);
    if (tempView?.config?.filters) {
      setFilters(tempView.config.filters);
    }
    if (tempView?.config?.searchTerm) {
      setSearchTerm(tempView.config.searchTerm);
    }
    if (tempView?.config?.groupBy) {
      setGroupBy(tempView.config.groupBy);
    }
  }, [selectedView]);

  const selectedColumns = useMemo(() => {
    if (columnsState.length > 0) {
      return columnsState;
    }
    const tempView = getTempView(selectedView);
    return tempView?.config?.columns || selectedView?.config?.columns || [];
  }, [columnsState, selectedView]);

  const serviceItemTypeNameById = useMemo(() => {
    const map = new Map<number, string>();
    serviceItemTypes.forEach((type) => {
      if (type?.id != null) map.set(Number(type.id), type.name);
    });
    return map;
  }, [serviceItemTypes]);

  const columns = useMemo<ColumnDef<ServiceData, unknown>[]>(() => {
    // These sit beside no metric column — per-service ARR/CARR are computed per
    // request — so they are grouped after the last money column, in metric order.
    const deltaColumnsFor = (
      metricKey: (typeof SERVICE_COMPARE_SLOTS)[number]["metricKey"],
      metricLabel: string,
      isTermsCurrency: boolean
    ): ColumnDef<ServiceData, unknown>[] =>
      buildDeltaColumns<ServiceData>({
        metricKey,
        // Column id and wire key are the same string here.
        columnId: metricKey,
        metricLabel,
        timeframe: compareTimeframeLabel,
        periodStart: comparisonPeriodStart,
        // Inside the memo: a fresh Date as a dependency rebuilds every column.
        periodEnd: toDateOnlyISO(new Date()),
        byRowId: comparisonsByServiceId,
        notFoundRowIds: comparisonNotFoundIdSet,
        isTermsCurrency,
        translationKey: SERVICE_COMPARE_TRANSLATION_KEY,
        // One Impact column per metric — the terms Δ describes the same movement.
        includeImpact: !isTermsCurrency,
        // The endpoint withholds New (no opening balances); the row's own start
        // date is what establishes it.
        allowNew: true,
        readRowStart: readServiceRowStart,
        // The design labels an Upcoming service New, so a term opening after
        // today still counts — unlike the contract grain.
        allowFutureStart: true,
        t
      });

    const compareColumns: ColumnDef<ServiceData, unknown>[] = compareToEnabled
      ? SERVICE_COMPARE_SLOTS.flatMap((slot) => [
          ...deltaColumnsFor(slot.metricKey, t(slot.labelKey), false),
          ...deltaColumnsFor(
            slot.termsMetricKey,
            termsCurrencyHeader(t(slot.labelKey), t),
            true
          )
        ])
      : [];

    return [
      {
        header: t(`services.${ServiceColumn.SERVICE_ITEM_NAME}`),
        accessorKey: ServiceColumn.SERVICE_ITEM_NAME,
        cell: (info) => info.row.original.serviceItemName || ""
      },
      {
        header: t(`services.${ServiceColumn.COMPANY_NAME}`),
        accessorKey: ServiceColumn.COMPANY_NAME,
        meta: { filterKey: "companyId" },
        cell: (info) =>
          resolveLinkedCellValue(
            info.row.original as unknown as Record<string, unknown>,
            "companyId",
            "companyName"
          )
      },
      {
        header: t(`services.${ServiceColumn.PRODUCT_NAME}`),
        accessorKey: ServiceColumn.PRODUCT_NAME,
        meta: { filterKey: "productId" },
        cell: (info) =>
          resolveLinkedCellValue(
            info.row.original as unknown as Record<string, unknown>,
            "productId",
            "productName"
          )
      },
      {
        header: t("services.serviceTypeName"),
        accessorKey: ServiceColumn.SERVICE_TYPE,
        meta: { filterKey: "serviceType" },
        cell: (info) => {
          const raw = info.row.original.serviceType;
          if (raw == null) return "";
          return serviceItemTypeNameById.get(Number(raw)) ?? "";
        }
      },
      {
        header: t("services.chargeTypeName"),
        accessorKey: ServiceColumn.CHARGE_TYPE,
        meta: { filterKey: "chargeType" },
        cell: (info) => {
          const raw = info.row.original.chargeType;
          if (raw == null) return "";
          return tWithFallback(
            getEnumTKey(
              "services",
              "chargeTypeValues",
              ChargeType,
              Number(raw) as ChargeType
            ),
            ""
          );
        }
      },
      {
        header: t(`services.${ServiceColumn.BILLING_FREQUENCY}`),
        id: ServiceColumn.BILLING_FREQUENCY,
        accessorFn: (row) => row.billingFrequencyDisplay ?? "",
        cell: (info) => info.getValue() ?? ""
      },
      {
        header: systemCurrencyHeader(t("services.pricing"), accountCurrency),
        id: ServiceColumn.PRICING,
        meta: { textAlign: "center" },
        cell: (info) => {
          const { total, quantity } = info.row.original;
          if (total == null || !quantity) return "";
          return <SystemCurrencyCell amount={total / quantity} />;
        }
      },
      {
        header: termsCurrencyHeader(t("services.pricing"), t),
        id: ServiceColumn.PRICING_TERMS,
        // Computed per row from the row's own currency, so there is nothing
        // comparable to order by — and the server has no `pricingLocal` field
        // to sort on, which would drop the sort while still showing an arrow.
        enableSorting: false,
        meta: { textAlign: "center" },
        cell: (info) => {
          const { totalLocal, quantity, currency } = info.row.original;
          if (totalLocal == null || !quantity) return "";
          return (
            <TermsCurrencyCell
              amount={totalLocal / quantity}
              currency={currency ?? null}
            />
          );
        }
      },
      {
        header: t(`services.${ServiceColumn.STATUS_NAME}`),
        accessorKey: ServiceColumn.STATUS_NAME,
        meta: { filterKey: "status" },
        cell: (info) => {
          const name = info.row.original.statusName;
          const status = info.row.original.status as ServiceStatus | null;
          if (!name) return "";
          const color =
            status != null ? serviceStatusColors[status] : undefined;
          return (
            <StatusBadge
              isTable
              statusColor={color}
              icon={status != null ? getServiceStatusIcon(status) : undefined}
            >
              {name}
            </StatusBadge>
          );
        }
      },
      {
        header: t(`services.${ServiceColumn.CURRENCY}`),
        accessorKey: ServiceColumn.CURRENCY,
        cell: (info) => {
          const code = info.row.original.currency;
          if (!code) return "";
          const symbol = getCurrencySymbol(code);
          return symbol && symbol !== code ? `${symbol} ${code}` : code;
        }
      },
      {
        header: t(`services.${ServiceColumn.CSM_OWNER}`),
        accessorKey: ServiceColumn.CSM_OWNER,
        cell: (info) => {
          const ownerId = info.row.original.csmOwner;
          if (!ownerId) return "";
          const user = userById.get(ownerId);
          const name =
            info.row.original.csmOwnerName || user?.displayName || "";
          return (
            <div className="flex items-center gap-2">
              <UserAvatar userData={user} size="M" />
              <span className="truncate">{name}</span>
            </div>
          );
        }
      },
      {
        header: t(`services.${ServiceColumn.AE_OWNER}`),
        accessorKey: ServiceColumn.AE_OWNER,
        cell: (info) => {
          const ownerId = info.row.original.aeOwner;
          if (!ownerId) return "";
          const user = userById.get(ownerId);
          const name = info.row.original.aeOwnerName || user?.displayName || "";
          return (
            <div className="flex items-center gap-2">
              <UserAvatar userData={user} size="M" />
              <span className="truncate">{name}</span>
            </div>
          );
        }
      },
      {
        header: t("services.customServiceDuration"),
        accessorKey: "customServiceDurationDisplay",
        cell: (info) => {
          const {
            customServiceDurationDisplay: fallback,
            customServiceDuration: raw,
            serviceStartDate,
            serviceEndDate
          } = info.row.original;
          const duration =
            raw != null ? getServiceDurationFromNumber(Number(raw)) : null;
          if (!duration) {
            if (fallback) return fallback;
            if (serviceStartDate && serviceEndDate) {
              return formatDistanceStrict(
                toLocalDate(serviceEndDate),
                toLocalDate(serviceStartDate)
              );
            }
            return "";
          }
          return tWithFallback(
            getEnumTKey(
              "services",
              "serviceDurationValues",
              ServiceDuration,
              duration
            ),
            fallback
          );
        }
      },
      {
        header: t("services.autoRenewal"),
        id: ServiceColumn.AUTO_RENEWAL_DISPLAY,
        accessorFn: renewalTypeLabel,
        cell: (info) => renewalTypeLabel(info.row.original)
      },
      {
        header: t("services.discountAmount"),
        accessorKey: "discountAmount",
        cell: (info) => {
          const amount = info.row.original.discountAmount;
          if (amount == null || amount === 0) return "";
          if (info.row.original.isDiscountPercentage)
            return formatPercentage(amount);
          const currency = info.row.original.currency;
          if (!currency) return String(amount);
          return displayTotalCurrency(amount, false, undefined, currency);
        }
      },
      ...(showNoteColumn
        ? [
            {
              header: t(`services.${ServiceColumn.NOTE}`),
              id: ServiceColumn.NOTE,
              enableSorting: false,
              cell: ServiceNoteCell
            }
          ]
        : []),
      ...compareColumns,
      ...mapSelectedCustomColumnsToTableColumns(
        selectedColumns,
        fieldsData?.fields,
        t,
        HARDCODED_COLUMN_IDS,
        groupBy,
        CURRENCY_FIELD_RESOLVERS,
        undefined,
        TERMS_COLUMN_IDS
      )
    ];
  }, [
    t,
    userById,
    selectedColumns,
    fieldsData?.fields,
    serviceItemTypeNameById,
    groupBy,
    accountCurrency,
    compareToEnabled,
    compareTimeframeLabel,
    comparisonPeriodStart,
    comparisonsByServiceId,
    comparisonNotFoundIdSet,
    showNoteColumn
  ]);

  const columnIds = useMemo<ColumnData[]>(
    () =>
      columns.map((col, order) => ({
        id: (col as { accessorKey?: string }).accessorKey ?? (col.id as string),
        // A Δ header is a render function once it has a tooltip, and String() on
        // one yields its source — those columns keep the label in meta.
        label:
          (col.meta as { label?: string } | undefined)?.label ??
          String(col.header ?? ""),
        order,
        isVisible: true
      })),
    [columns]
  );

  const filterOptions = useMemo(
    () =>
      buildBaseFilterOptions({
        t,
        products,
        userFilterValues,
        currencies,
        serviceItemTypes,
        dealStages
      }),
    [t, products, userFilterValues, currencies, serviceItemTypes, dealStages]
  );

  const customFieldsForFilters = useMemo(
    () =>
      (fieldsData?.fields ?? []).filter(
        (field) => !HARDCODED_COLUMN_IDS.has(camelizeFieldKey(field.fieldKey))
      ),
    [fieldsData?.fields]
  );

  // Base filters carry enum value→label maps; custom-field filters carry the
  // select-option maps. The export needs both to render labels instead of codes.
  const exportFilterOptions = useMemo(
    () => [
      ...filterOptions,
      ...mapCustomFieldsToFilterOptions(columnsState, customFieldsForFilters)
    ],
    [filterOptions, columnsState, customFieldsForFilters]
  );

  // A terms Δ starts hidden wherever this table's other terms columns do.
  const termsHiddenByDefaultIds = useMemo(() => {
    const ids = new Set<string>(TERMS_COLUMN_IDS);
    if (compareToEnabled) {
      SERVICE_COMPARE_TERMS_COLUMN_IDS.forEach((id) => ids.add(id));
    }
    return ids;
  }, [compareToEnabled]);

  const defaultColumnsState = useMemo<ColumnData[]>(() => {
    const customOnlyFields = (fieldsData?.fields ?? []).filter(
      (field) => !HARDCODED_COLUMN_IDS.has(camelizeFieldKey(field.fieldKey))
    );
    const compareIds = new Set(compareToColumnIds);
    const state = getDefaultColumnsStateWithCustomFields(
      columnIds
        .map((c) => c.id)
        // A Δ id is neither hardcoded nor a registry field, so the filter drops it.
        .filter((id) => HARDCODED_COLUMN_IDS.has(id) || compareIds.has(id)),
      customOnlyFields,
      "services",
      t,
      undefined,
      undefined,
      termsColumnsVisibleByDefault ? undefined : termsHiddenByDefaultIds,
      new Map(columnIds.map((column) => [column.id, column.label]))
    );
    const apiFieldKeyMap = new Map(
      customOnlyFields.map((f) => [camelizeFieldKey(f.fieldKey), f])
    );
    // Total arrives from the registry, after every hardcoded column; the Note
    // column sits right after it. A saved view gains Note in the same place,
    // since the merge inserts a missing column after its default predecessor.
    const noteIndex = state.findIndex((col) => col.id === ServiceColumn.NOTE);
    if (noteIndex !== -1) {
      const [noteColumn] = state.splice(noteIndex, 1);
      const totalIndex = state.findIndex(
        (col) => col.id === ServiceColumn.TOTAL
      );
      state.splice(
        totalIndex === -1 ? state.length : totalIndex + 1,
        0,
        noteColumn
      );
      state.forEach((stateColumn, order) => {
        state[order] = { ...stateColumn, order };
      });
    }
    return state.map((col) => {
      if (col.isVisible) return col;
      // The terms-hidden-by-default gate above is meant to be the final word
      // on a terms column's visibility (see getDefaultColumnsStateWithCustomFields),
      // so a terms id must never be re-shown here regardless of its registry
      // field type — otherwise a SYSTEM/FORMULA/LINKED terms field (e.g.
      // totalLocal) escapes the gate the moment its detail-view flag flips on.
      if (termsHiddenByDefaultIds.has(col.id)) return col;
      const field = apiFieldKeyMap.get(col.id);
      if (!field) return col;
      if (
        field.fieldType === SettingsFieldType.SYSTEM ||
        field.fieldType === SettingsFieldType.FORMULA ||
        (field.fieldType === SettingsFieldType.LINKED &&
          field.showInDetailedView)
      ) {
        return { ...col, isVisible: true };
      }
      return col;
    });
  }, [
    columnIds,
    fieldsData?.fields,
    t,
    termsColumnsVisibleByDefault,
    termsHiddenByDefaultIds,
    compareToColumnIds
  ]);

  useEffect(() => {
    if (!fieldsData?.fields?.length) return;
    if (reconciledForViewRef.current === selectedView.id) return;
    const tempView = getTempView(selectedView);
    const persistedColumns =
      tempView?.config?.columns ?? selectedView.config?.columns;
    if (persistedColumns?.length) {
      // The relabel below reads accountCurrency off `columnIds` to refresh the
      // system-currency headers (`Pricing (USD)`) — waiting for it here is the
      // same reasoning as the currencyUsageResolved wait below: this write is
      // a one-time snapshot into columnsState, and an unresolved currency
      // would freeze the bare, no-currency label in it permanently, unlike
      // the live grid header which just re-renders once the setting loads.
      if (!accountCurrencyResolved) return;
      reconciledForViewRef.current = selectedView.id;
      const currentLabelById = new Map(columnIds.map((c) => [c.id, c.label]));
      setColumnsState(
        persistedColumns.map((col: ColumnData) => ({
          ...col,
          label: currentLabelById.get(col.id) ?? col.label
        }))
      );
      return;
    }
    // A saved view says what the columns are, so it loads whatever currency
    // usage is doing. Only seeding the default layout waits for it: the terms
    // columns default to visible while the read is in flight, and a seeded
    // layout is never rewritten. The view stays unreconciled until then.
    if (!currencyUsageResolved || !accountCurrencyResolved) return;
    reconciledForViewRef.current = selectedView.id;
    if (defaultColumnsState.length > 0) {
      // Seeds only an empty layout. With views already cached, the toolbar
      // activates the saved view in this same commit while `selectedView` here
      // is still the initial id-less one, so its columns are already queued.
      setColumnsState((prev) => (prev.length === 0 ? defaultColumnsState : prev));
    }
  }, [
    fieldsData?.fields?.length,
    selectedView,
    defaultColumnsState,
    columnIds,
    currencyUsageResolved,
    accountCurrencyResolved
  ]);

  useEffect(() => {
    // Same gate the seeding branch above applies: a terms column folded into a
    // saved layout before currency usage resolves is folded in visible, and
    // that guess survives the next save. accountCurrencyResolved guards the
    // same one-time-snapshot hazard as the reconciliation effect above: while
    // it's unresolved, defaultColumnsState can still be holding a bare,
    // no-currency label that this merge would fold into columnsState for good.
    if (!currencyUsageResolved || !accountCurrencyResolved) return;
    setColumnsState((prev) =>
      mergeMissingDefaultColumns(prev, defaultColumnsState)
    );
  }, [defaultColumnsState, currencyUsageResolved, accountCurrencyResolved]);

  return (
    <>
      <ServicesBottomKPIBar
        aggregations={aggregations}
        isLoading={isLoading || isAggregationsLoading}
        isError={isAggregationsError}
      />
      <DataTableToolbar
        viewType={ViewType.SERVICES_TABLE}
        selectedView={selectedView}
        setSelectedView={setSelectedView}
        filterOptions={filterOptions}
        filters={filters}
        setFilters={setFilters}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        groupBy={groupBy}
        setGroupBy={setGroupBy}
        sorting={sorting}
        onSortingChange={setSorting}
        defaultColumnsState={defaultColumnsState}
        columnsState={columnsState}
        setColumnsState={setColumnsState}
        sortByOptions={sortByOptions}
        defaultSortByValue={ServiceColumn.SERVICE_ITEM_NAME}
        tableId={TABLE_ID}
        customFields={customFieldsForFilters}
      />
      <ToolbarActions
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filters={filters}
        setFilters={setFilters}
        filterOptions={filterOptions}
        groupBy={groupBy}
        setGroupBy={setGroupBy}
        sorting={sorting}
        onSortingChange={setSorting}
        sortByOptions={sortByOptions}
        defaultSortByValue={ServiceColumn.SERVICE_ITEM_NAME}
        columnsState={columnsState}
        setColumnsState={setColumnsState}
        defaultColumnsState={defaultColumnsState}
        selectedView={selectedView}
        setSelectedView={setSelectedView}
        viewType={ViewType.SERVICES_TABLE}
        customFields={customFieldsForFilters}
        groupByFields={fieldsData?.fields}
        tableId={TABLE_ID}
        className="px-4 py-2"
        compareTo={compareToEnabled ? compareTo : undefined}
        onCompareToChange={compareToEnabled ? setCompareTo : undefined}
        defaultCompareTo={DEFAULT_COMPARE_TO}
        sortMenuExcludedColumnIds={compareToSortMenuExclusions}
      />
      <DataTable
        key={TABLE_ID}
        dataTest="services-table"
        columns={columns as ColumnDef<unknown, unknown>[]}
        data={services}
        manualSorting
        initialSortBy={sorting}
        onSortingChange={setSorting}
        isInfiniteScroll
        hasMore={hasNextPage}
        isDataLoading={isLoading}
        isLoadingMore={isFetchingNextPage}
        onLoadMore={handleLoadMore}
        loadingThreshold={100}
        resizableColumns
        stickyLeftColumn
        enableColumnReorder
        headerClassName="border-none"
        className="h-full"
        filterOptions={filterOptions}
        filters={filters}
        setFilters={setFilters}
        hasActiveFilters={!isEmpty(filters)}
        onClearFilters={handleClearFilters}
        onClearSearch={() => setSearchTerm("")}
        isError={isError}
        onRetry={refetch}
        onColumnsChange={setColumnsState}
        groupBy={groupBy}
        groupingConfig={{
          typeLabel: t("services.bottomKPI.total"),
          aggregationKey: "total",
          aggregationLabel: t("services.bottomKPI.totalValue"),
          formatAggregation: (value: number) => displayTotalCurrency(value),
          groups: knownGroups,
          groupSummaries,
          subtotalAggregations: [
            {
              key: "totalValue",
              label: t("services.bottomKPI.totalValue"),
              aggregate: (rows) =>
                rows.reduce((acc, row) => acc + (row.total || 0), 0),
              fromSums: (sums) => sums.total,
              format: (value) => displayTotalCurrency(value)
            }
          ]
        }}
        tableId={TABLE_ID}
        selectedView={selectedView}
        columnsState={columnsState}
      />
    </>
  );
};

export default ServicesTable;
